import React from 'react';
import { motion } from 'framer-motion';
import { BsPersonPlus, BsStarFill } from 'react-icons/bs';

interface SmartMatchingProps {
    suggestions?: any[];
}

const SmartMatchingPanel: React.FC<SmartMatchingProps> = ({ suggestions = [] }) => {
    return (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 backdrop-blur-md sticky top-24">
            <div className="flex justify-between items-center mb-6">
                <h3 className="font-serif text-xl font-bold text-white">Suggested Talent</h3>
                <span className="text-xs text-premium-gold bg-premium-gold/10 px-2 py-1 rounded-full border border-premium-gold/20">AI Matched</span>
            </div>

            <p className="text-sm text-white/50 mb-6">
                Based on your budget history and active projects.
            </p>

            <div className="space-y-4">
                {suggestions.length === 0 ? (
                    <div className="text-center p-4 text-white/50 italic border border-white/5 rounded-lg">
                        Publish an opportunity or task to get AI-matched talent suggestions.
                    </div>
                ) : (
                    suggestions.map((talent: any, index: number) => (
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            key={talent.id}
                            className="p-4 bg-black/20 rounded-lg border border-white/5 hover:border-premium-gold/30 transition-all group"
                        >
                            <div className="flex gap-4">
                                <div className="relative">
                                    {talent.avatar ? (
                                        <img src={talent.avatar} alt={talent.name} className="w-12 h-12 rounded-full border border-premium-gold/50 object-cover" />
                                    ) : (
                                        <div className="w-12 h-12 rounded-full border border-premium-gold/50 bg-white/10 flex items-center justify-center text-white/50 text-xl font-bold">
                                            {talent.name ? talent.name.charAt(0).toUpperCase() : '?'}
                                        </div>
                                    )}
                                    <div className="absolute -bottom-2 -right-2 bg-black rounded-full p-0.5">
                                        <div className="bg-green-500 w-3 h-3 rounded-full border-2 border-black"></div>
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h4 className="font-bold text-white text-sm">{talent.name}</h4>
                                            <div className="text-xs text-premium-gold">{talent.role}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xs font-bold text-green-400">{talent.matchPercent}% Match</div>
                                            <div className="flex items-center gap-1 text-[10px] text-white/60 mt-0.5">
                                                <BsStarFill className="text-premium-gold w-3 h-3" /> {talent.rating}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-xs text-white/40 mt-2 flex justify-between">
                                        <span>{talent.location}</span>
                                        <span>{talent.availability}</span>
                                    </div>

                                    <button className="w-full mt-3 py-1.5 text-xs font-bold text-white bg-white/10 hover:bg-premium-gold hover:text-black rounded-lg transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 h-0 group-hover:h-auto overflow-hidden">
                                        <BsPersonPlus /> Quick Hire
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))
                )}
            </div>

            {suggestions.length > 0 && (
                <button className="w-full mt-6 py-3 text-sm font-bold text-premium-gold border border-premium-gold/30 rounded-lg hover:bg-premium-gold hover:text-black transition-colors">
                    View All Matches
                </button>
            )}
        </div>
    );
};

export default SmartMatchingPanel;
